import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { apiFetch } from '@/lib/operator-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, ChevronUp, ChevronDown, Trash2, Pencil, GitBranch,
  Circle, MessageCircle, GitFork, Zap, Square, Save, BarChart3, ExternalLink,
} from 'lucide-react';
import type { FlowTabProps, FlowDetail, EditableNode } from './types';

// -- Constants ----------------------------------------------------------------

const NODE_TYPES = ['start', 'interaction', 'decision', 'action', 'terminal'] as const;

const NODE_TYPE_CONFIG: Record<string, { icon: typeof Circle; color: string; bgColor: string }> = {
  start: { icon: Circle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  interaction: { icon: MessageCircle, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  decision: { icon: GitFork, color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
  action: { icon: Zap, color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  terminal: { icon: Square, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
};

// -- Helpers ------------------------------------------------------------------

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

// -- API helpers (scoped to edit operations) -----------------------------------

async function fetchFlowDetail(slug: string, bizId: string): Promise<FlowDetail> {
  const res = await apiFetch(`/api/operator/flows/${encodeURIComponent(slug)}?businessId=${encodeURIComponent(bizId)}`);
  if (!res.ok) throw new Error('Failed to load flow');
  return (await res.json()).flow;
}

async function updateFlow(slug: string, bizId: string, data: {
  name?: string; namePtBr?: string; description?: string; descriptionPtBr?: string;
  isActive?: boolean; nodes?: Omit<EditableNode, '_isNew'>[];
}): Promise<FlowDetail> {
  const res = await apiFetch(`/api/operator/flows/${encodeURIComponent(slug)}?businessId=${encodeURIComponent(bizId)}`, {
    method: 'PUT', body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update flow');
  return (await res.json()).flow;
}

async function fetchFlowCounts(slug: string, bizId: string): Promise<{ counts: Record<string, number>; total: number }> {
  try {
    const res = await apiFetch(`/api/operator/flows/${encodeURIComponent(slug)}/counts?businessId=${encodeURIComponent(bizId)}`);
    if (!res.ok) return { counts: {}, total: 0 };
    const json = await res.json();
    return { counts: json.counts || {}, total: json.total || 0 };
  } catch {
    return { counts: {}, total: 0 };
  }
}

// -- NodeTypeBadge ------------------------------------------------------------

function NodeTypeBadge({ nodeType }: { nodeType: string }) {
  const { t } = useTranslation();
  const config = NODE_TYPE_CONFIG[nodeType] || NODE_TYPE_CONFIG.action;
  const Icon = config.icon;
  return (
    <Badge variant="secondary" className={`${config.bgColor} ${config.color} border-0 gap-1`}>
      <Icon className="h-3 w-3" />
      {t(`dynamicFlows.nodeTypes.${nodeType}`, nodeType)}
    </Badge>
  );
}

// -- NodeTypeSelector ---------------------------------------------------------

function NodeTypeSelector({ value, onChange }: { value: string; onChange: (t: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-5 gap-2">
      {NODE_TYPES.map((type) => {
        const config = NODE_TYPE_CONFIG[type];
        const Icon = config.icon;
        const sel = value === type;
        return (
          <button key={type} type="button" onClick={() => onChange(type)}
            className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-center ${
              sel ? `border-primary ${config.bgColor} ${config.color}`
                  : 'border-muted hover:border-muted-foreground/30 text-muted-foreground hover:text-foreground'
            }`}>
            <Icon className="h-5 w-5" />
            <span className="text-xs font-medium leading-tight">{t(`dynamicFlows.nodeTypes.${type}`, type)}</span>
          </button>
        );
      })}
    </div>
  );
}

// -- InlineNodeEditor ---------------------------------------------------------

function InlineNodeEditor({ node, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast }: {
  node: EditableNode; onChange: (u: EditableNode) => void; onDelete: () => void;
  onMoveUp: () => void; onMoveDown: () => void; isFirst: boolean; isLast: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(!!node._isNew);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="border rounded-lg overflow-hidden transition-all">
      {/* Collapsed row */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={onMoveUp} disabled={isFirst}
            className="p-0.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
            title={t('dynamicFlows.editor.moveUp')}>
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={onMoveDown} disabled={isLast}
            className="p-0.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
            title={t('dynamicFlows.editor.moveDown')}>
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
        <span className="text-xs text-muted-foreground font-mono w-6 text-center">{node.position + 1}</span>
        <NodeTypeBadge nodeType={node.nodeType} />
        <span className="flex-1 font-medium text-sm truncate">{node.name}</span>
        {node.messageText && (
          <span className="text-xs text-muted-foreground truncate max-w-[200px] hidden md:inline">
            {node.messageText.slice(0, 60)}{node.messageText.length > 60 ? '...' : ''}
          </span>
        )}
        {node.templateSlug && (
          <Link to="/operator/templates" onClick={(e) => e.stopPropagation()} className="shrink-0 hidden sm:inline-flex">
            <Badge variant="outline" className="text-[10px] font-mono gap-1 hover:border-primary hover:text-primary transition-colors cursor-pointer">
              {t('dynamicFlows.editor.templatePrefix', 'Template')}: {node.templateSlug}
            </Badge>
          </Link>
        )}
        <Pencil className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t space-y-4 animate-in fade-in-0 slide-in-from-top-2 duration-200">
          <div className="space-y-2">
            <Label>{t('dynamicFlows.editor.nodeType')}</Label>
            <NodeTypeSelector value={node.nodeType} onChange={(type) => onChange({ ...node, nodeType: type })} />
          </div>

          <div className="space-y-2">
            <Label>{t('dynamicFlows.editor.linkedTemplate', 'Linked Template')}</Label>
            <div className="flex gap-2">
              <Input value={node.templateSlug || ''} onChange={(e) => onChange({ ...node, templateSlug: e.target.value || undefined })}
                placeholder="e.g., greeting, confirmation" className="font-mono text-sm flex-1" />
              {node.templateSlug && (
                <Link to="/operator/templates"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md border text-xs font-medium text-primary hover:bg-primary/5 transition-colors shrink-0">
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t('dynamicFlows.editor.editTemplate', 'Edit Template')}
                </Link>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('dynamicFlows.editor.linkedTemplateHint', 'Optional: slug of a message template to use at this step.')}
            </p>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('dynamicFlows.editor.nodeNameEn')}</Label>
              <Input value={node.name} onChange={(e) => onChange({ ...node, name: e.target.value })}
                placeholder={t('dynamicFlows.editor.nodeNamePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('dynamicFlows.editor.nodeNamePtBr')}</Label>
              <Input value={node.namePtBr} onChange={(e) => onChange({ ...node, namePtBr: e.target.value })}
                placeholder={t('dynamicFlows.editor.nodeNamePtBrPlaceholder')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('dynamicFlows.editor.messageEn')}</Label>
              <Textarea value={node.messageText} onChange={(e) => onChange({ ...node, messageText: e.target.value })}
                placeholder={t('dynamicFlows.editor.messagePlaceholder')} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>{t('dynamicFlows.editor.messagePtBr')}</Label>
              <Textarea value={node.messageTextPtBr} onChange={(e) => onChange({ ...node, messageTextPtBr: e.target.value })}
                placeholder={t('dynamicFlows.editor.messagePtBrPlaceholder')} rows={3} />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4 mr-1" />
              {t('dynamicFlows.editor.deleteNode')}
            </Button>
          </div>

          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('dynamicFlows.editor.deleteConfirmTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('dynamicFlows.editor.deleteConfirmDescription', { name: node.name })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => { setConfirmDelete(false); onDelete(); }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  {t('common.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

// -- FlowEditTab (main export) ------------------------------------------------

export function FlowEditTab({ flow, flowSlug, businessId, onFlowUpdated }: FlowTabProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [nodes, setNodes] = useState<EditableNode[]>([]);
  const [flowName, setFlowName] = useState(flow.name);
  const [flowNamePtBr, setFlowNamePtBr] = useState(flow.namePtBr || '');
  const [flowDescription, setFlowDescription] = useState(flow.description);
  const [flowDescriptionPtBr, setFlowDescriptionPtBr] = useState(flow.descriptionPtBr || '');
  const [counts, setCounts] = useState<{ counts: Record<string, number>; total: number }>({ counts: {}, total: 0 });
  const [hasChanges, setHasChanges] = useState(false);

  // Load flow detail and counts
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([fetchFlowDetail(flowSlug, businessId), fetchFlowCounts(flowSlug, businessId)])
      .then(([detail, countsData]) => {
        if (cancelled) return;
        setFlowName(detail.name);
        setFlowNamePtBr(detail.namePtBr || '');
        setFlowDescription(detail.description);
        setFlowDescriptionPtBr(detail.descriptionPtBr || '');
        setNodes(
          detail.nodes.sort((a, b) => a.position - b.position).map((n) => ({
            id: n.id, slug: n.slug, name: n.name, namePtBr: n.namePtBr || '',
            nodeType: n.nodeType, position: n.position,
            messageText: n.messageText || '', messageTextPtBr: n.messageTextPtBr || '',
            templateSlug: n.templateSlug || undefined,
          })),
        );
        setCounts(countsData);
        setHasChanges(false);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t('dynamicFlows.errors.loadFailed'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [flowSlug, businessId, t]);

  const handleNodeChange = useCallback((index: number, updated: EditableNode) => {
    setNodes((prev) => { const next = [...prev]; next[index] = updated; return next; });
    setHasChanges(true);
  }, []);

  const handleNodeDelete = useCallback((index: number) => {
    setNodes((prev) => prev.filter((_, i) => i !== index).map((n, i) => ({ ...n, position: i })));
    setHasChanges(true);
  }, []);

  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return;
    setNodes((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((n, i) => ({ ...n, position: i }));
    });
    setHasChanges(true);
  }, []);

  const handleMoveDown = useCallback((index: number) => {
    setNodes((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((n, i) => ({ ...n, position: i }));
    });
    setHasChanges(true);
  }, []);

  const handleAddNode = useCallback((atIndex?: number) => {
    const insertAt = atIndex !== undefined ? atIndex : nodes.length;
    const newNode: EditableNode = {
      slug: `node-${Date.now()}`, name: '', namePtBr: '', nodeType: 'interaction',
      position: insertAt, messageText: '', messageTextPtBr: '', _isNew: true,
    };
    setNodes((prev) => {
      const next = [...prev];
      next.splice(insertAt, 0, newNode);
      return next.map((n, i) => ({ ...n, position: i }));
    });
    setHasChanges(true);
  }, [nodes.length]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const sanitizedNodes = nodes.map(({ _isNew, ...rest }) => ({
        ...rest, slug: rest.slug || slugify(rest.name) || `node-${rest.position}`,
      }));
      await updateFlow(flowSlug, businessId, {
        name: flowName, namePtBr: flowNamePtBr || undefined,
        description: flowDescription, descriptionPtBr: flowDescriptionPtBr || undefined,
        nodes: sanitizedNodes,
      });
      setHasChanges(false);
      setSuccessMessage(t('dynamicFlows.editor.saveSuccess'));
      onFlowUpdated();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dynamicFlows.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [nodes, flowSlug, businessId, flowName, flowNamePtBr, flowDescription, flowDescriptionPtBr, onFlowUpdated, t]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-primary shrink-0" />
          <h2 className="text-lg font-semibold">{flow.name}</h2>
          {hasChanges && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
              {t('dynamicFlows.editor.unsavedChanges')}
            </Badge>
          )}
        </div>
        {counts.total > 0 && (
          <Badge variant="outline" className="gap-1">
            <BarChart3 className="h-3 w-3" />
            {t('dynamicFlows.stats.conversations', { count: counts.total })}
          </Badge>
        )}
      </div>

      {/* Flow metadata */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('dynamicFlows.editor.flowNameEn')}</Label>
            <Input value={flowName} onChange={(e) => { setFlowName(e.target.value); setHasChanges(true); }} />
          </div>
          <div className="space-y-2">
            <Label>{t('dynamicFlows.editor.flowNamePtBr')}</Label>
            <Input value={flowNamePtBr} onChange={(e) => { setFlowNamePtBr(e.target.value); setHasChanges(true); }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('dynamicFlows.editor.flowDescEn')}</Label>
            <Textarea value={flowDescription} onChange={(e) => { setFlowDescription(e.target.value); setHasChanges(true); }} rows={2} />
          </div>
          <div className="space-y-2">
            <Label>{t('dynamicFlows.editor.flowDescPtBr')}</Label>
            <Textarea value={flowDescriptionPtBr} onChange={(e) => { setFlowDescriptionPtBr(e.target.value); setHasChanges(true); }} rows={2} />
          </div>
        </div>
      </div>

      <Separator />

      {/* Node list header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('dynamicFlows.editor.nodesTitle', { count: nodes.length })}</h3>
        <Button size="sm" variant="outline" onClick={() => handleAddNode()}>
          <Plus className="h-4 w-4 mr-1" />
          {t('dynamicFlows.editor.addNode')}
        </Button>
      </div>

      {/* Node list */}
      {nodes.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <GitBranch className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('dynamicFlows.editor.noNodes')}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => handleAddNode()}>
            <Plus className="h-4 w-4 mr-1" />{t('dynamicFlows.editor.addFirstNode')}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {nodes.map((node, index) => (
            <div key={node.id || `new-${index}`}>
              <InlineNodeEditor node={node}
                onChange={(updated) => handleNodeChange(index, updated)}
                onDelete={() => handleNodeDelete(index)}
                onMoveUp={() => handleMoveUp(index)}
                onMoveDown={() => handleMoveDown(index)}
                isFirst={index === 0} isLast={index === nodes.length - 1} />
              {index < nodes.length - 1 && (
                <div className="flex justify-center py-1">
                  <button type="button" onClick={() => handleAddNode(index + 1)}
                    className="group flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    title={t('dynamicFlows.editor.insertNodeHere')}>
                    <Plus className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="border-b border-dashed border-muted-foreground/30 group-hover:border-primary/50">
                      {t('dynamicFlows.editor.insertHere')}
                    </span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Error / Success messages */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-4 py-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="rounded-md bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 px-4 py-3">
          <p className="text-sm text-green-600 dark:text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Save button -- no cancel; tab navigation handles going back */}
      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} disabled={saving || !hasChanges}>
          <Save className="h-4 w-4 mr-1" />
          {saving ? t('dynamicFlows.editor.saving') : t('dynamicFlows.editor.save')}
        </Button>
      </div>
    </div>
  );
}

export default FlowEditTab;
